import '../tools/Tools.css';
import { useReducer, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// 도구(뽀모도로 + 집중 사운드) — 기존 Tools 페이지 콘텐츠를 커뮤니티 탭으로 이전.
// 페이지/헤더/사이드바 래퍼는 Community 가 제공하므로 여기서는 본문만 렌더한다.

const WORK_SECONDS = 25 * 60;
const BREAK_SECONDS = 5 * 60;

/**
 * 유튜브 영상 ID — 제공해 주신 링크 기준.
 * 카테고리 label·트랙 title 은 i18n(tools 네임스페이스)에서 id 로 조회한다.
 */
const FOCUS_SOUND_CATEGORIES = [
  {
    id: 'playlist',
    tracks: [
      { id: 'pl-1', youtubeId: 'Yuw8TnTei58' },
      { id: 'pl-2', youtubeId: 'Go_8LaJRl7E' },
      { id: 'pl-3', youtubeId: 'jim9K1vBL0k' },
      { id: 'pl-4', youtubeId: '0QmTzpr7qGY' },
    ],
  },
  {
    id: 'piano',
    tracks: [
      { id: 'pi-1', youtubeId: 'Q-iEfzaikBI' },
      { id: 'pi-2', youtubeId: 'wnudr9qjrbA' },
      { id: 'pi-3', youtubeId: 'Vzv5HHehLg8' },
    ],
  },
  {
    id: 'asmr',
    tracks: [
      { id: 'as-1', youtubeId: '5ss2qOtJf4U' },
      { id: 'as-2', youtubeId: 'bAyyZeAaii4' },
    ],
  },
  {
    id: 'focus',
    tracks: [{ id: 'fc-1', youtubeId: 'UQCidibMWVE' }],
  },
  {
    id: 'nap',
    tracks: [
      { id: 'np-1', youtubeId: 'SVmjYoZlj6I' },
      { id: 'np-2', youtubeId: 'cy9icl7T0Qs' },
    ],
  },
];

const DEFAULT_FOCUS_YOUTUBE_ID = FOCUS_SOUND_CATEGORIES[0].tracks[0].youtubeId;

function formatTime(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function getInitialPomodoroState() {
  return {
    mode: 'work',
    seconds: WORK_SECONDS,
    isRunning: false,
    completedCount: 0,
    beepToken: 0,
  };
}

function pomodoroReducer(state, action) {
  switch (action.type) {
    case 'tick':
      if (state.seconds <= 1) {
        if (state.mode === 'work') {
          return {
            ...state,
            mode: 'break',
            seconds: BREAK_SECONDS,
            isRunning: true,
            completedCount: state.completedCount + 1,
            beepToken: state.beepToken + 1,
          };
        }
        return {
          ...state,
          mode: 'work',
          seconds: WORK_SECONDS,
          isRunning: true,
          beepToken: state.beepToken + 1,
        };
      }
      return { ...state, seconds: state.seconds - 1 };
    case 'toggle':
      return { ...state, isRunning: !state.isRunning };
    case 'reset':
      return {
        ...state,
        seconds: state.mode === 'work' ? WORK_SECONDS : BREAK_SECONDS,
        isRunning: false,
      };
    case 'set-mode': {
      if (state.isRunning) return state;
      const next = action.mode;
      return {
        ...state,
        mode: next,
        seconds: next === 'work' ? WORK_SECONDS : BREAK_SECONDS,
      };
    }
    default:
      return state;
  }
}

const POMODORO_BEEP_COUNT = 3;
const POMODORO_BEEP_GAP_SEC = 1;
/** 게인 피크 (0~1). 크게 할수록 비프가 커짐 */
const POMODORO_BEEP_PEAK = 0.32;

/** 띠띠 — 짧은 비프 3번 (Web Audio API) */
function playPomodoroBeeps() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();

    const schedule = () => {
      const now = ctx.currentTime;
      const beep = (t0) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        // 더 날카롭게 — square wave + 높은 주파수(1318Hz / E6)
        osc.type = 'square';
        osc.frequency.setValueAtTime(1318, t0);
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(POMODORO_BEEP_PEAK, t0 + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.13);
        osc.start(t0);
        osc.stop(t0 + 0.14);
      };
      for (let i = 0; i < POMODORO_BEEP_COUNT; i++) {
        beep(now + i * POMODORO_BEEP_GAP_SEC);
      }
      const closeAfterMs =
        Math.ceil(((POMODORO_BEEP_COUNT - 1) * POMODORO_BEEP_GAP_SEC + 0.2) * 1000) + 400;
      setTimeout(() => {
        ctx.close().catch(() => {});
      }, closeAfterMs);
    };

    if (ctx.state === 'suspended') {
      void ctx.resume().then(() => schedule());
    } else {
      schedule();
    }
  } catch {
    /* no audio */
  }
}

const RING_R = 108;
const RING_C = 2 * Math.PI * RING_R;

export default function ToolsTab() {
  const { t } = useTranslation('tools');
  const [focusYoutubeId, setFocusYoutubeId] = useState(DEFAULT_FOCUS_YOUTUBE_ID);
  const [focusCategoryId, setFocusCategoryId] = useState(FOCUS_SOUND_CATEGORIES[0].id);
  const [pomodoro, dispatch] = useReducer(pomodoroReducer, null, getInitialPomodoroState);

  useEffect(() => {
    if (!pomodoro.isRunning) return;
    const id = window.setInterval(() => dispatch({ type: 'tick' }), 1000);
    return () => window.clearInterval(id);
  }, [pomodoro.isRunning]);

  useEffect(() => {
    if (pomodoro.beepToken > 0) {
      playPomodoroBeeps();
    }
  }, [pomodoro.beepToken]);

  const totalForMode = pomodoro.mode === 'work' ? WORK_SECONDS : BREAK_SECONDS;
  const progressRatio = totalForMode > 0 ? pomodoro.seconds / totalForMode : 0;
  const dashOffset = RING_C * (1 - progressRatio);

  return (
    <section className="tools-pomodoro" aria-labelledby="tools-pomodoro-title">
      <div className="tools-focus-grid">
        <div className="tools-focus-col tools-focus-col--pomodoro">
          <div className="pomodoro-card">
            <h1 id="tools-pomodoro-title" className="pomodoro-heading">
              {t('pomodoro.heading')}
            </h1>
            <p className="pomodoro-desc">
              {t('pomodoro.descPrefix')}{' '}
              <button
                type="button"
                className="pomodoro-beep-preview"
                onClick={() => playPomodoroBeeps()}
                aria-label={t('pomodoro.beepPreviewAria')}
              >
                {t('pomodoro.beepPreview')}
              </button>
              <br />
              {t('pomodoro.descReset')}
            </p>

            <div
              className={`pomodoro-mode-toggle${pomodoro.isRunning ? ' pomodoro-mode-toggle--disabled' : ''}`}
              role="group"
              aria-label={t('pomodoro.modeGroupAria')}
            >
              <button
                type="button"
                className={`pomodoro-mode-btn pomodoro-mode-btn--work${pomodoro.mode === 'work' ? ' is-active' : ''}`}
                disabled={pomodoro.isRunning}
                onClick={() => dispatch({ type: 'set-mode', mode: 'work' })}
              >
                {t('pomodoro.modeWork')}
              </button>
              <button
                type="button"
                className={`pomodoro-mode-btn pomodoro-mode-btn--break${pomodoro.mode === 'break' ? ' is-active' : ''}`}
                disabled={pomodoro.isRunning}
                onClick={() => dispatch({ type: 'set-mode', mode: 'break' })}
              >
                {t('pomodoro.modeBreak')}
              </button>
            </div>

            <div
              className={`pomodoro-ring-section${pomodoro.mode === 'break' ? ' pomodoro-ring-section--break' : ''}`}
            >
              <div className="pomodoro-ring-wrap" aria-hidden>
                <svg className="pomodoro-ring-svg" viewBox="0 0 240 240">
                  <circle
                    className="pomodoro-ring-track"
                    cx="120"
                    cy="120"
                    r={RING_R}
                    fill="none"
                  />
                  <circle
                    className={
                      pomodoro.mode === 'work'
                        ? 'pomodoro-ring-progress pomodoro-ring-progress--work'
                        : 'pomodoro-ring-progress pomodoro-ring-progress--break'
                    }
                    cx="120"
                    cy="120"
                    r={RING_R}
                    fill="none"
                    strokeDasharray={RING_C}
                    strokeDashoffset={dashOffset}
                    transform="rotate(-90 120 120)"
                  />
                </svg>
                <div className="pomodoro-ring-center">
                  <span className="pomodoro-timer-label">
                    {pomodoro.mode === 'work' ? t('pomodoro.focusTime') : t('pomodoro.breakTime')}
                  </span>
                  <span className="pomodoro-timer-digits">{formatTime(pomodoro.seconds)}</span>
                  <span className="pomodoro-cumulative" aria-live="polite">
                    {t('pomodoro.cumulative', { count: pomodoro.completedCount })}
                  </span>
                </div>
              </div>
            </div>

            <div className="pomodoro-actions">
              <button
                type="button"
                className={`pomodoro-btn pomodoro-btn-primary${pomodoro.mode === 'break' ? ' pomodoro-btn-primary--break' : ''}`}
                onClick={() => dispatch({ type: 'toggle' })}
              >
                {pomodoro.isRunning ? t('pomodoro.pause') : t('pomodoro.start')}
              </button>
              <button
                type="button"
                className="pomodoro-btn pomodoro-btn-outline"
                onClick={() => dispatch({ type: 'reset' })}
              >
                {t('pomodoro.reset')}
              </button>
            </div>
          </div>
        </div>

        <div className="tools-focus-col tools-focus-col--sound">
          <div className="focus-sound-card" aria-labelledby="focus-sound-title">
            <h2 id="focus-sound-title" className="focus-sound-heading">
              {t('focusSound.heading')}
            </h2>
            <p className="focus-sound-desc tools-multiline">{t('focusSound.desc')}</p>

            {/* 카테고리 — 가로 스크롤 탭. 선택 시 아래 트랙 리스트만 노출 */}
            <div
              className="focus-sound-cat-tabs"
              role="tablist"
              aria-label={t('focusSound.categoryTabsAria')}
            >
              {FOCUS_SOUND_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  role="tab"
                  aria-selected={focusCategoryId === cat.id}
                  className={`focus-sound-cat-tab${focusCategoryId === cat.id ? ' is-active' : ''}`}
                  onClick={() => setFocusCategoryId(cat.id)}
                >
                  {t(`focusSound.categories.${cat.id}`)}
                </button>
              ))}
            </div>

            <ul className="focus-sound-track-list">
              {(FOCUS_SOUND_CATEGORIES.find((c) => c.id === focusCategoryId)?.tracks || []).map(
                (track) => (
                  <li key={track.id} className="focus-sound-track-item">
                    <button
                      type="button"
                      className={`focus-sound-track-btn${focusYoutubeId === track.youtubeId ? ' is-active' : ''}`}
                      onClick={() => setFocusYoutubeId(track.youtubeId)}
                    >
                      {t(`focusSound.tracks.${track.id}`)}
                    </button>
                  </li>
                )
              )}
            </ul>

            <div className="focus-sound-player-wrap">
              <iframe
                key={focusYoutubeId}
                title={t('focusSound.playerTitle')}
                className="focus-sound-iframe"
                src={`https://www.youtube.com/embed/${focusYoutubeId}?rel=0`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
